<script setup>
import { defineProps, computed } from 'vue';
import { handleMarketplaceUrl } from '@/services/api';

const props = defineProps({
  items: {
    type: Array,
    default: () => []
  }
});

const normalizedItems = computed(() => {
  if (!props.items) return [];
  
  let itemsArray = [];
  if (Array.isArray(props.items)) {
    itemsArray = props.items;
  } else if (Array.isArray(props.items?.listings)) {
    itemsArray = props.items.listings;
  }
  
  return itemsArray.map((item, index) => ({
    id: item.id ?? index,
    name: item.name || item.title || item.badge || 'Untitled'
  }));
});

const hasItems = computed(() => {
  return normalizedItems.value.length > 0;
});

const handleItemClick = (itemId) => {
  const baseUrl = handleMarketplaceUrl();
  window.open(`${baseUrl}/item-detail/${itemId}`, '_blank');
};
</script>

<template>
  <div class="infinite-carousel" v-if="hasItems">
    <div class="carousel-wrapper">
      <div class="carousel-content">
        <div
          v-for="(item, index) in [...normalizedItems, ...normalizedItems, ...normalizedItems]"
          :key="`item-${item.id}-${index}`"
          class="carousel-item"
          @click="handleItemClick(item.id)"
        >
          {{ item.name }}
        </div>
      </div>
    </div>
  </div>
  <div v-else class="carousel-placeholder">
    <div class="card-placeholder"></div>
  </div>
</template>

<style scoped>
.infinite-carousel {
  width: 100%;
  min-height: 3rem;
  height: 100%;
  overflow: hidden;
  position: relative;
}

.carousel-wrapper {
  width: 100%;
  height: 100%;
  overflow: hidden;
  position: relative;
}

.carousel-wrapper::before,
.carousel-wrapper::after {
  content: '';
  position: absolute;
  top: 0;
  bottom: 0;
  width: 60px;
  z-index: 10;
  pointer-events: none;
}

.carousel-wrapper::before {
  left: 0;
  background: linear-gradient(to right, rgba(0, 0, 0, 0.8) 0%, rgba(0, 0, 0, 0.6) 20%, rgba(0, 0, 0, 0.4) 40%, rgba(0, 0, 0, 0.2) 60%, rgba(0, 0, 0, 0.1) 80%, rgba(0, 0, 0, 0) 100%);
}

.carousel-wrapper::after {
  right: 0;
  background: linear-gradient(to left, rgba(0, 0, 0, 0.8) 0%, rgba(0, 0, 0, 0.6) 20%, rgba(0, 0, 0, 0.4) 40%, rgba(0, 0, 0, 0.2) 60%, rgba(0, 0, 0, 0.1) 80%, rgba(0, 0, 0, 0) 100%);
}

@media (min-width: 640px) {
  .carousel-wrapper::before,
  .carousel-wrapper::after {
    width: 120px;
  }
  
  .carousel-wrapper::before {
    background: linear-gradient(to right, rgba(0, 0, 0, 0.95) 0%, rgba(0, 0, 0, 0.9) 15%, rgba(0, 0, 0, 0.8) 30%, rgba(0, 0, 0, 0.6) 50%, rgba(0, 0, 0, 0.4) 70%, rgba(0, 0, 0, 0.2) 85%, rgba(0, 0, 0, 0) 100%);
  }
  
  .carousel-wrapper::after {
    background: linear-gradient(to left, rgba(0, 0, 0, 0.95) 0%, rgba(0, 0, 0, 0.9) 15%, rgba(0, 0, 0, 0.8) 30%, rgba(0, 0, 0, 0.6) 50%, rgba(0, 0, 0, 0.4) 70%, rgba(0, 0, 0, 0.2) 85%, rgba(0, 0, 0, 0) 100%);
  }
}

@media (min-width: 1024px) {
  .carousel-wrapper::before,
  .carousel-wrapper::after {
    width: 250px;
  }
  
  .carousel-wrapper::before {
    background: linear-gradient(to right, rgba(0, 0, 0, 1) 0%, rgba(0, 0, 0, 0.98) 10%, rgba(0, 0, 0, 0.95) 20%, rgba(0, 0, 0, 0.85) 35%, rgba(0, 0, 0, 0.7) 50%, rgba(0, 0, 0, 0.5) 65%, rgba(0, 0, 0, 0.3) 80%, rgba(0, 0, 0, 0.1) 90%, rgba(0, 0, 0, 0) 100%);
  }
  
  .carousel-wrapper::after {
    background: linear-gradient(to left, rgba(0, 0, 0, 1) 0%, rgba(0, 0, 0, 0.98) 10%, rgba(0, 0, 0, 0.95) 20%, rgba(0, 0, 0, 0.85) 35%, rgba(0, 0, 0, 0.7) 50%, rgba(0, 0, 0, 0.5) 65%, rgba(0, 0, 0, 0.3) 80%, rgba(0, 0, 0, 0.1) 90%, rgba(0, 0, 0, 0) 100%);
  }
}

.carousel-content {
  display: inline-flex;
  align-items: center;
  gap: 1rem;
  min-height: 3rem;
  height: 100%;
  animation: scroll 30s linear infinite;
  will-change: transform;
}

@media (min-width: 640px) {
  .carousel-content {
    gap: 1.5rem;
  }
}

@media (min-width: 1024px) {
  .carousel-content {
    gap: 2rem;
  }
}

.infinite-carousel:hover .carousel-content {
  animation-play-state: paused;
}

.carousel-item {
  flex-shrink: 0;
  width: fit-content;
  min-height: 2.5rem;
  height: fit-content;
  font-size: 0.875rem;
  text-decoration: underline;
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;
  transition: opacity 0.2s ease;
  padding: 0.5rem 0;
  display: flex;
  align-items: center;
  color: rgba(255, 255, 255, 0.8);
}

@media (min-width: 640px) {
  .carousel-item {
    font-size: 1rem;
  }
}

.carousel-item:hover {
  opacity: 0.8;
}

@keyframes scroll {
  0% {
    transform: translateX(0);
  }
  100% {
    transform: translateX(calc(-100% / 3));
  }
}

.carousel-placeholder {
  width: 100%;
  min-height: 3rem;
  height: 100%;
  display: flex;
  align-items: center;
  position: relative;
  visibility: visible;
}

.card-placeholder {
  width: 200px;
  min-height: 2.5rem;
  height: 2.5rem;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 0.5rem;
  animation: pulse 1.5s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% {
    opacity: 0.4;
  }
  50% {
    opacity: 0.8;
  }
}
</style>
